/**
 * The wire to our own API.
 *
 * One rule in this file: the anon Supabase key is the only key here, and it is
 * public. The bearer token is whatever the signed-in account holds, so a mistake
 * in a URL or a body reaches this service as an ordinary user request — which RLS
 * and the API's own checks then refuse. Nothing in this bundle can read the
 * catalogue of licences, grant one, or see another account's order.
 *
 * ## Why the failure sentences come from the dictionary
 *
 * Every message a person reads on a failure is prose, and prose belongs to the i18n
 * layer like everything else. This module has no hook to call and no props to take a
 * dictionary through, so it reads the active one from `i18n` — see the note on
 * `activeDictionary` there for why that is safe and why it is still a service
 * locator done deliberately rather than one reached for by habit.
 *
 * What does *not* pass through here is a token, an internal identifier, or a raw
 * provider response that was not recognised. A sentence that reaches a screen is
 * either ours or the provider's own, never a token echoed back by an intermediary.
 */

import { activeDictionary } from '../i18n/active.ts';
import type { Dictionary } from '../i18n/types.ts';

/** The failure sentences this module can produce, by name rather than by value. */
export type ApiMessageKey = keyof Dictionary['api'];

const SUPABASE_URL = 'https://api.devk.com.br';
const SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiIsImlzcyI6InN1cGFiYXNlIiwiaWF0IjoxNzgxMTY0ODgzLCJleHAiOjIwOTY1MjQ4ODN9.jKmcI5RJj_1PIjjS5xqcQ_IfCEBp8u_c-G5oOEp_Mv4';

/** Same origin in production; overridable so a preview build can point elsewhere. */
const API_BASE = import.meta.env['VITE_API_BASE'] ?? '/api';

/**
 * Thrown for every failure the user can be shown a sentence about.
 *
 * It carries the *key* of a sentence, never the sentence. That is what lets an error
 * survive being held in state: `message` is a getter that resolves the key against
 * whichever dictionary is current at the moment it is read, so an error raised while
 * the page was in Portuguese and read after the reader switched to English is
 * reported in English.
 *
 * A resolved string stored in state is the alternative, and it is wrong: the chrome
 * around the message re-renders in the new language and the message does not, so the
 * page shows a Spanish sentence under an English heading.
 *
 * `serverMessage` is the one exception and it is deliberate. When our own API sends a
 * message this file does not recognise, its wording is passed through rather than
 * replaced with a guess, and React escapes it on the way to the screen.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly messageKey: ApiMessageKey;
  /** Our own API's wording, when it supplied one this file does not recognise. */
  readonly serverMessage: string | null;

  constructor(
    messageKey: ApiMessageKey,
    code: string,
    status: number,
    serverMessage: string | null = null,
  ) {
    // The key goes to `super` only because `Error` demands an argument; the own
    // `message` it leaves behind is replaced below.
    super(messageKey);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.messageKey = messageKey;
    this.serverMessage = serverMessage;
    Object.defineProperty(this, 'message', {
      get: () => this.serverMessage ?? activeDictionary().api[this.messageKey],
      enumerable: true,
      configurable: true,
    });
  }
}

/**
 * The sentence for anything that was thrown, in the language on screen now.
 *
 * Callers hold the thrown value, not its message, for the reason `ApiError.message`
 * is a getter. `fallback` covers the case of a throw that was never an `Error` at all.
 */
export function messageOf(caught: unknown, fallback: string): string {
  return caught instanceof Error ? caught.message : fallback;
}

interface ErrorBody {
  error?: { code?: string; message?: string };
}

/** One place every response is turned into either data or an ApiError. */
async function call<T>(path: string, init: RequestInit = {}, token?: string | null): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined) headers.set('Content-Type', 'application/json');
  if (token != null && token !== '') headers.set('Authorization', `Bearer ${token}`);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'omit' });
  } catch {
    // No status code to report, so the message has to say what to do rather than
    // what happened.
    throw new ApiError('offline', 'offline', 0);
  }

  const text = await response.text();
  let body: unknown = null;
  if (text !== '') {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    const parsed = body as ErrorBody | null;
    // Our own API's wording when it gave any, the local sentence when it did not.
    throw new ApiError(
      'unexpected',
      parsed?.error?.code ?? 'unknown',
      response.status,
      parsed?.error?.message ?? null,
    );
  }

  return body as T;
}

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

export interface Product {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly unlocks: readonly string[];
  readonly amountCents: number;
  readonly currency: string;
}

export interface Catalogue {
  readonly products: readonly Product[];
  readonly paymentPublicKey: string;
}

export function fetchProducts(): Promise<Catalogue> {
  return call<Catalogue>('/products');
}

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------

export interface Account {
  readonly id: string;
  readonly email: string | null;
  readonly isOperator: boolean;
}

export interface Entitlement {
  readonly appId: string;
  readonly plan: string;
  readonly expiresAt: string | null;
}

export interface AccountResponse {
  readonly account: Account;
  readonly entitlements: readonly Entitlement[];
}

export function fetchAccount(token: string): Promise<AccountResponse> {
  return call<AccountResponse>('/account', {}, token);
}

export interface AppEntry {
  readonly id: string;
  readonly name: string;
  readonly minimumVersion: string;
}

export function fetchApps(token: string): Promise<{ apps: readonly AppEntry[] }> {
  return call<{ apps: readonly AppEntry[] }>('/apps', {}, token);
}

// ---------------------------------------------------------------------------
// Checkout and orders
// ---------------------------------------------------------------------------

export interface CheckoutResponse {
  readonly orderId: string;
  readonly checkoutUrl: string;
  readonly amountCents: number;
  readonly currency: string;
  readonly unlocks: readonly string[];
}

export function startCheckout(token: string, productId: string): Promise<CheckoutResponse> {
  return call<CheckoutResponse>('/checkout', { method: 'POST', body: JSON.stringify({ productId }) }, token);
}

export type OrderStatus = 'pending' | 'approved' | 'rejected' | 'refunded' | 'expired';

export interface Order {
  readonly id: string;
  readonly productId: string;
  readonly status: OrderStatus;
  readonly amountCents: number;
  readonly currency: string;
  readonly unlocks: readonly string[];
  readonly createdAt: string;
  readonly paidAt: string | null;
}

export function fetchOrder(token: string, orderId: string): Promise<{ order: Order }> {
  return call<{ order: Order }>(`/orders/${encodeURIComponent(orderId)}`, {}, token);
}

// ---------------------------------------------------------------------------
// The account itself, held in Supabase
//
// Sign-in goes straight to GoTrue rather than through our API: it is the same
// auth install the two desktop apps use, so one set of credentials is one account
// everywhere. Our API only ever verifies the token it is handed.
// ---------------------------------------------------------------------------

export interface Session {
  readonly accessToken: string;
  readonly email: string | null;
  /**
   * Kept so this browser's session can be renewed.
   *
   * The two desktop apps both run a refresh loop and this page did not, which meant
   * the shared account behaved differently on every surface: an hour after signing in,
   * the apps were still working and this page quietly signed the reader out and asked
   * for the password again. One account is supposed to mean one thing, and a session
   * that expires while the app next to it is still signed in is the clearest evidence
   * that the three are not the same system.
   *
   * A refresh token in `localStorage` is the same exposure the apps accepted for their
   * own session file — it is readable by anything running as this user, and it is
   * revoked by signing out. It is not a service credential: it can only mint this
   * account's own sessions, which is what a session already is.
   */
  readonly refreshToken: string | null;
  /** Absolute expiry, in milliseconds. Read from the service rather than measured locally. */
  readonly expiresAt: number;
}

/** Where the session lives between reloads. */
const STORAGE_KEY = 'orobas.session';

/**
 * How long before expiry a token is renewed.
 *
 * The same five minutes the desktop apps use, and for the same reason: a page that
 * fires a request on the last second of a token's life gets a 401 and reads as being
 * signed out at random.
 */
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

export function readStoredSession(): Session | null {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const record = parsed as Partial<Session>;
    if (typeof record.accessToken !== 'string' || record.accessToken === '') return null;
    return {
      accessToken: record.accessToken,
      email: typeof record.email === 'string' ? record.email : null,
      refreshToken: typeof record.refreshToken === 'string' ? record.refreshToken : null,
      // A session written by a build before this one has no expiry. Treated as "expires
      // now" so it is refreshed on first use rather than trusted for an unknown length.
      expiresAt: typeof record.expiresAt === 'number' ? record.expiresAt : 0,
    };
  } catch {
    // Corrupt storage is not an error worth surfacing; it is a signed-out state.
    return null;
  }
}

export function writeStoredSession(session: Session | null): void {
  if (session === null) window.localStorage.removeItem(STORAGE_KEY);
  else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

/** Whether this browser can renew its own session without the reader's password. */
export function canRenew(session: Session | null): session is Session {
  return session !== null && session.refreshToken !== null;
}

/** Whether the access token is close enough to expiry to be worth renewing. */
export function needsRenewal(session: Session, now: number = Date.now()): boolean {
  return now + REFRESH_MARGIN_MS >= session.expiresAt;
}

/** GoTrue speaks snake_case; this is the shape it actually sends. */
interface GoTrueToken {
  access_token?: unknown;
  refresh_token?: unknown;
  expires_in?: unknown;
  expires_at?: unknown;
  email?: unknown;
}

/**
 * Reads a token payload into a session.
 *
 * `expires_at` is a number of seconds since the epoch on this deployment; `expires_in` is
 * the fallback, measured from the moment the answer arrives. The absolute form is
 * preferred because a machine whose clock is off would otherwise compute its own
 * expiry — and the access token is the one thing here whose expiry has to be right.
 */
function sessionFrom(body: GoTrueToken, email: string | null): Session {
  if (typeof body.access_token !== 'string' || body.access_token === '') {
    throw new ApiError('signInFailed', 'sign_in_failed', 502);
  }
  const absolute = typeof body.expires_at === 'number' ? body.expires_at * 1000 : null;
  const relative = typeof body.expires_in === 'number' ? Date.now() + body.expires_in * 1000 : null;

  return {
    accessToken: body.access_token,
    email: typeof body.email === 'string' ? body.email : email,
    refreshToken: typeof body.refresh_token === 'string' ? body.refresh_token : null,
    expiresAt: absolute ?? relative ?? 0,
  };
}

export async function signInWithPassword(email: string, password: string): Promise<Session> {
  const body = await gotrue<GoTrueToken>('/token?grant_type=password', { email, password });
  return sessionFrom(body, email);
}

/**
 * Trades the stored refresh token for a new access token.
 *
 * The one failure worth naming: GoTrue refuses a refresh token it has already rotated,
 * so this can fail on a session that is perfectly valid. That is a signed-out state
 * rather than an error to show — the reader is asked for the password again, which is
 * the honest response to a credential the server will no longer accept.
 */
export async function renewSession(session: Session): Promise<Session> {
  if (session.refreshToken === null) {
    throw new ApiError('sessionExpired', 'no_refresh_token', 401);
  }
  const body = await gotrue<GoTrueToken>('/token?grant_type=refresh_token', {
    refresh_token: session.refreshToken,
  });
  return sessionFrom(body, session.email);
}

/**
 * Creates the account.
 *
 * The server has autoconfirm off, so this always ends on "check your e-mail" rather
 * than signed in — which is the service's setting, shared with everything else on
 * this host, and not something this page can change for itself.
 *
 * The `redirect_to` query is the return address GoTrue builds into the
 * confirmation link. Without it the verified reader lands on the GoTrue host's
 * own homepage (devk.com.br) with a session in the hash and no explanation; with
 * it they return to /conta and can sign in from a page they recognise.
 */
export async function signUpWithPassword(email: string, password: string): Promise<void> {
  try {
    await gotrue('/signup?redirect_to=https%3A%2F%2Fwww.orobas.app%2Fconta', { email, password });
  } catch (error) {
    // GoTrue answers 422 with a list of reasons. "already registered" is one of
    // them, and it must not be reported as a failure: the person can sign in.
    if (error instanceof ApiError && error.status === 422 && /registered/i.test(error.message)) {
      throw new ApiError('alreadyRegistered', 'already_registered', 422);
    }
    throw error;
  }
}

// ---------------------------------------------------------------------------
// OAuth sign-in (Google, GitHub)
// ---------------------------------------------------------------------------

/**
 * Keys under which the PKCE verifier and the OAuth state are kept while the
 * browser is away at the provider. sessionStorage survives the redirect back to
 * the callback page and is cleared as soon as the exchange is done, so a stale
 * verifier or state cannot be replayed.
 */
const OAUTH_VERIFIER_KEY = 'orobas.oauth_verifier';
const OAUTH_STATE_KEY = 'orobas.oauth_state';

/** 43–128 unreserved base64url characters, as RFC 7636 requires for a verifier. */
function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** SHA-256 of the verifier, base64url-encoded without padding (S256 challenge). */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Starts a Google or GitHub sign-in.
 *
 * Builds a PKCE pair plus an anti-CSRF state, keeps both in sessionStorage, and
 * sends the browser to GoTrue's authorize endpoint. GoTrue hands the browser to
 * the provider, and the provider sends it back to the callback URL with a code
 * that `completeOAuthSignIn` exchanges for a session.
 *
 * This never returns: it navigates the browser away.
 */
export async function signInWithOAuth(provider: 'google' | 'github'): Promise<void> {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateCodeVerifier();
  sessionStorage.setItem(OAUTH_VERIFIER_KEY, verifier);
  sessionStorage.setItem(OAUTH_STATE_KEY, state);

  const redirectTo = `${window.location.origin}/auth/oauth-callback`;
  const params = new URLSearchParams({
    provider,
    redirect_to: redirectTo,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });

  window.location.href = `${SUPABASE_URL}/auth/v1/authorize?${params.toString()}`;
}

/**
 * Trades the code the provider left in the callback URL for a session.
 *
 * The verifier is the one `signInWithOAuth` stored before navigating away; the
 * state is the anti-CSRF nonce. Without either, GoTrue rejects the exchange.
 * Both are consumed here so neither can be replayed.
 */
export async function completeOAuthSignIn(code: string, state: string): Promise<Session> {
  const verifier = sessionStorage.getItem(OAUTH_VERIFIER_KEY);
  const storedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  if (verifier === null || storedState === null) {
    throw new ApiError('signInFailed', 'oauth_missing_verifier', 400);
  }
  if (state !== storedState) {
    throw new ApiError('signInFailed', 'oauth_state_mismatch', 400);
  }
  sessionStorage.removeItem(OAUTH_VERIFIER_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);

  const body = await gotrue<GoTrueToken>('/token?grant_type=pkce', {
    code,
    code_verifier: verifier,
  });
  return sessionFrom(body, null);
}

async function gotrue<T>(path: string, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${SUPABASE_URL}/auth/v1${path}`, {
      method: 'POST',
      headers: { apikey: SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError('accountOffline', 'account_offline', 0);
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    const failure = describeGoTrueError(parsed, response.status);
    throw new ApiError(failure.key, 'auth_failed', response.status, failure.providerText);
  }

  return parsed as T;
}

/**
 * Turns GoTrue's answer into something a person can act on.
 *
 * The cases named below are the ones that would otherwise read as "erro de
 * autenticação", which tells the reader nothing and sends them to the wrong remedy.
 * Everything else passes through as the provider's own wording: a mapping table here
 * would have to guess between meanings forever, and a wrong guess is worse than the
 * provider saying it itself.
 *
 * A key, not a sentence, so the sentence is resolved when it is read rather than when
 * the error was built. What never passes through is a token or an internal identifier.
 */
function describeGoTrueError(body: unknown, status: number): {
  readonly key: ApiMessageKey;
  readonly providerText: string | null;
} {
  if (typeof body === 'object' && body !== null) {
    const record = body as { msg?: unknown; message?: unknown; error_description?: unknown };
    for (const candidate of [record.msg, record.message, record.error_description]) {
      if (typeof candidate !== 'string' || candidate === '') continue;

      if (/invalid login credentials/i.test(candidate)) return named('badCredentials');
      if (/email not confirmed/i.test(candidate)) return named('emailNotConfirmed');
      if (/password should be/i.test(candidate)) return named('passwordTooShort');
      if (/unable to validate email|invalid email/i.test(candidate)) return named('invalidEmail');
      if (/rate limit|too many|security purposes/i.test(candidate)) return named('rateLimited');
      return { key: 'unexpected', providerText: candidate };
    }
  }

  if (status === 429) return named('rateLimited');
  if (status === 0) return named('accountOffline');
  return named('signInFailed');
}

function named(key: ApiMessageKey): { readonly key: ApiMessageKey; readonly providerText: null } {
  return { key, providerText: null };
}

export { SUPABASE_URL, SUPABASE_ANON_KEY };
export { call };
